import { useState } from "react";
import { isSafePictureUrl } from "@saas-on-sheets/line-helpers";
import { Banner } from "./Banner.tsx";

export interface LineLinkCardProps {
  lineLinked: boolean;
  lineDisplayName?: string;
  linePictureUrl?: string;
  lineLinkedAt?: number;
  onGenerate: () => Promise<{ code: string; expiresAt: number }>;
  onUnlink: () => Promise<void>;
  onRefresh: () => Promise<void>;
}

export function LineLinkCard(props: LineLinkCardProps) {
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleGenerate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { code: c } = await props.onGenerate();
      setCode(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate code");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnlink(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await props.onUnlink();
      await props.onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unlink");
    } finally {
      setBusy(false);
    }
  }

  async function handleRefresh(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await props.onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh");
    } finally {
      setBusy(false);
    }
  }

  function handleCopy(): void {
    if (!code) return;
    void navigator.clipboard?.writeText(`/link ${code}`);
  }

  if (props.lineLinked) {
    const name = props.lineDisplayName ?? "(unknown)";
    const linkedSince =
      props.lineLinkedAt !== undefined
        ? new Intl.DateTimeFormat(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(props.lineLinkedAt))
        : null;

    return (
      <section className="card" aria-labelledby="line-link-title">
        <div className="card__header">
          <h2 id="line-link-title">LINE integration</h2>
        </div>
        <div className="line-linked">
          {isSafePictureUrl(props.linePictureUrl) ? (
            <img className="line-linked__avatar" src={props.linePictureUrl} alt={name} />
          ) : (
            <span className="line-linked__avatar line-linked__avatar--fallback" aria-hidden="true">
              {name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="line-linked__info">
            <p>
              Linked as <strong>{name}</strong>
            </p>
            {linkedSince ? <p className="muted">Linked since {linkedSince}</p> : null}
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={handleRefresh} disabled={busy}>
            {busy ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={handleUnlink}
            disabled={busy}
          >
            {busy ? "Unlinking…" : "Unlink"}
          </button>
        </div>
        {error ? <Banner kind="error">{error}</Banner> : null}
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="line-link-title">
      <div className="card__header">
        <h2 id="line-link-title">LINE integration</h2>
      </div>
      {!code ? (
        <>
          <p>เชื่อม LINE bot เพื่อ submit ข้อความ/รูปจาก LINE</p>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleGenerate}
              disabled={busy}
            >
              {busy ? "Generating…" : "Generate code"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            พิมพ์ <code>/link {code}</code> ใน LINE bot ภายใน 10 นาที
          </p>
          <div className="form-actions">
            <button type="button" className="btn btn--secondary" onClick={handleCopy}>
              Copy
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleRefresh}
              disabled={busy}
            >
              {busy ? "Refreshing…" : "Refresh status"}
            </button>
          </div>
        </>
      )}
      {error ? <Banner kind="error">{error}</Banner> : null}
    </section>
  );
}
